import { toDecimalPlaces } from 'utils/calculator';
import { getPricePerPax } from './SNSummatedOverridePrice';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { retrieveExchangeFeePerCarrier } from 'utils/summatedFaresExchangePrice';

export const getPrice = ({ legs, cancelled = false }) =>
  legs?.reduce(
    (acc, { products }) =>
      acc + products?.reduce((accPrice, { price, originalPrice }) => accPrice + (cancelled ? price : originalPrice), 0),
    0
  );

export const ESorERPricesComparison = ({ creationLeg, cancellationLeg }) =>
  creationLeg
    .map(({ products }) =>
      products.map(({ passengerId, originalPrice }) => {
        const correspondingCancelledPrice = getPricePerPax({ legs: cancellationLeg, passengerId, cancelled: true });

        return {
          originalPrice,
          correspondingCancelledPrice,
          exchangingToCheaper: originalPrice < correspondingCancelledPrice
        };
      })
    )
    ?.flat();

export const pricesSNSummatedFares = ({
  legsBeingExchanged,
  fareDataIsSummated,
  fareData,
  isExchangingMultipleLegsSummated,
  isExchangingAThroughFare,
  newFareTotal,
  originalFareTotal,
  defaultDisplayPrice,
  carrier = serviceTypeCodes.EUROSTAR,
  isExchangingSingleSNLegOfASummatedFare = false,
  oneExchangedItemHasAOPActive = false, // (i.e. either outbound or inbound, we apply)
  isExchangingSameClassOfService = false
}) => {
  // We cannot use the fare difference without any rebook computation
  // we need to display the price that will be charged or refunded
  // this fare can be < 0 (Only for the SN leg only of a SN summated fare)
  if (isExchangingSingleSNLegOfASummatedFare) {
    const getSNExchangeFees = retrieveExchangeFeePerCarrier(legsBeingExchanged, serviceTypeCodes.SNCF);
    const SNCancellationLegs = legsBeingExchanged.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);
    const SNCreationLegs = fareData.legs.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);
    const createdSNPrices = getPrice({ legs: SNCreationLegs });
    const cancelledSNPrices = getPrice({ legs: SNCancellationLegs, cancelled: true });

    return toDecimalPlaces(2, createdSNPrices - cancelledSNPrices + getSNExchangeFees);
  }

  if (!fareDataIsSummated) return defaultDisplayPrice;

  const overrideExchangeFees = oneExchangedItemHasAOPActive;
  const getSNExchangeFees = retrieveExchangeFeePerCarrier(legsBeingExchanged, serviceTypeCodes.SNCF, overrideExchangeFees);
  const getExchangeFees = retrieveExchangeFeePerCarrier(legsBeingExchanged, carrier, overrideExchangeFees);

  // We can use the fare difference without any rebook computation
  // this fare can never be < 0
  if (isExchangingAThroughFare) {
    return oneExchangedItemHasAOPActive && isExchangingSameClassOfService
      ? 0
      : toDecimalPlaces(2, Math.max(0, newFareTotal - originalFareTotal) + (getExchangeFees + getSNExchangeFees));
  }

  // We cannot use the fare difference without any rebook computation
  // we need to display the price that will be charged or refunded
  // when the cheaper ES or ER fare will be overriden
  // This can happen only after the rebook + overridePrice
  // therefore we need to do the same fare calculation logic here to match the 2 screens
  // this fare can be < 0
  if (isExchangingMultipleLegsSummated) {
    const ESorERCancelledLegs = legsBeingExchanged.filter(({ serviceType }) => serviceType.code === carrier);
    const ESorERCreationLegs = fareData.legs.filter(({ serviceType }) => serviceType.code === carrier);
    const SNCancellationLegs = legsBeingExchanged.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);
    const SNCreationLegs = fareData.legs.filter(({ serviceType }) => serviceType.code === serviceTypeCodes.SNCF);

    const cancelledESorERPrices = getPrice({ legs: ESorERCancelledLegs, cancelled: true });
    const createdESorERPrices = ESorERPricesComparison({ creationLeg: ESorERCreationLegs, cancellationLeg: ESorERCancelledLegs });
    const cancelledSNPrices = getPrice({ legs: SNCancellationLegs, cancelled: true });
    const createdSNPrices =
      oneExchangedItemHasAOPActive && isExchangingSameClassOfService ? cancelledSNPrices : getPrice({ legs: SNCreationLegs });

    const createdESorERPricesAfterRebook =
      oneExchangedItemHasAOPActive && isExchangingSameClassOfService
        ? cancelledESorERPrices
        : createdESorERPrices?.reduce(
            (acc, { exchangingToCheaper, correspondingCancelledPrice, originalPrice }) =>
              acc + (exchangingToCheaper ? correspondingCancelledPrice : originalPrice),
            0
          );

    return toDecimalPlaces(
      2,
      createdSNPrices - cancelledSNPrices + (createdESorERPricesAfterRebook - cancelledESorERPrices) + (getExchangeFees + getSNExchangeFees)
    );
  }

  return defaultDisplayPrice;
};
